import { FC, useMemo, useState } from 'react'

import { styled } from 'styled-components'

import { R } from 'core/helpers'
import { LoadingContainer } from 'peach/components'
import { StyledButton } from 'peach/components/Button/Button'

import { DynamicForm, PageHeader } from 'core/components'
import { ErrorComponent } from './Components'
import RoleConfig from './RoleConfig'
import { Company } from './types'
import {
  useUserPermissions_get,
  useCompanyRoleAdd,
  useCompanyRoleGetAll,
} from 'core/types'
import { isForbiddenError } from 'core/helpers/error'

type CreateRoleFormProps = {
  company: Company
  updateCompany: (obj: Company) => void
  title: string
  alias?: string
}

const CreateRoleForm: FC<CreateRoleFormProps> = ({
  company,
  updateCompany,
  title,
  alias,
}) => {
  const {
    data: roles,
    isLoading: isLoadingRoles,
    isError: isErrorRoles,
    error: errorRoles,
    refetch: refetchRoles,
  } = useCompanyRoleGetAll({
    companyId: company?.companyId,
    query: { limit: 100 },
    options: {
      enabled: !!company?.companyId,
      throwOnError: R.isNot(isForbiddenError),
    },
  })

  const { isLoading: isLoadingPermissions, isError: isErrorPermissions } =
    useUserPermissions_get({})

  const {
    mutateAsync: createRole,
    isPending,
    isError: isErrorCreateRole,
    error: errorCreateRole,
  } = useCompanyRoleAdd({
    companyId: company?.companyId,
  })

  const [formKey, setFormKey] = useState(0)
  const handleReset = () => setFormKey((k) => k + 1)

  const initialData = alias
    ? { ...RoleConfig[alias], baseRoleId: undefined }
    : { alias: '', actions: [], baseRoleId: undefined }

  // Prepare role options for baseRoleId select (used in JSON/Raw modes even though field is hidden in Form mode)
  const roleOptions = useMemo(() => {
    if (!roles || !Array.isArray(roles)) return []
    return roles.map((role) => ({
      label: role.alias || role.id || 'Unnamed role',
      value: role.id || '',
    }))
  }, [roles])

  const onSubmit = async (values: any) => {
    try {
      const resp = await createRole(values)

      if (resp?.status === 201 && resp?.data?.id) {
        updateCompany({
          ...company,
          roles: [
            ...R.pipe(
              company?.roles ?? [],
              R.reject((role) => role?.alias === alias),
            ),
            {
              alias,
              id: resp.data.id,
            },
          ],
        })

        refetchRoles?.()
      }
    } catch (e) {
      console.error('Error with create role: ', e)
      throw e
    }
  }

  if (isLoadingPermissions || isLoadingRoles) {
    return <Loading>Loading...</Loading>
  }

  // For 403 errors on roles list, we can still proceed with role creation
  // The new company may not have roles yet, but we can create the first one
  const isForbiddenRolesError =
    isErrorRoles &&
    errorRoles &&
    typeof errorRoles === 'object' &&
    'status' in errorRoles &&
    errorRoles.status === 403

  if (isErrorPermissions) {
    return (
      <ErrorComponent>
        Whoops! Error fetching permissions. Looks like you don't have the right
        permissions to continue.
      </ErrorComponent>
    )
  }

  if (isErrorRoles && !isForbiddenRolesError) {
    return (
      <>
        <ErrorComponent>
          Whoops! Error fetching roles. Looks like you don't have the right
          permissions to continue.
        </ErrorComponent>
        <StyledButton onClick={() => refetchRoles?.()}>
          Refetch roles
        </StyledButton>
      </>
    )
  }

  return (
    <LoadingContainer loading={isPending}>
      <PageHeader
        action={
          <StyledButton onClick={handleReset} type='button'>
            Reset Form
          </StyledButton>
        }
        title={title}
      />
      <BorderlessFormWrapper>
        <DynamicForm
          allowTabSwitchInAllMode
          data={initialData}
          fieldEditMode='all'
          fieldOrder={['alias', 'actions']}
          fieldOverrides={{
            actions: 'JsonInput',
          }}
          ignoreIsDirty
          key={formKey}
          omitFields={[
            'id',
            'createdAt',
            'updatedAt',
            'deletedAt',
            'companyId',
            'baseRoleId',
          ]}
          onSubmit={onSubmit}
          selectOptions={{
            baseRoleId: roleOptions,
          }}
          submitText='Create Role'
          validationRules={{
            alias: (v: unknown) => {
              // Alias is optional in API, but if provided must be a valid string <= 100 chars
              if (v === undefined || v === null || v === '') return true
              if (typeof v !== 'string') return 'Alias must be a string'
              if (v.length > 100) return 'Alias must be <= 100 characters'
              return true
            },
            actions: (v: unknown, formState) => {
              // Validate array structure if provided
              if (v !== undefined && v !== null) {
                if (!Array.isArray(v)) return 'Actions must be an array'
                if (!v.every((action: unknown) => typeof action === 'string')) {
                  return 'All actions must be strings'
                }
              }
              // Either baseRoleId or non-empty actions required
              const hasActions = Array.isArray(v) && v.length > 0
              const hasBaseRole = !!formState.values.baseRoleId
              if (!hasActions && !hasBaseRole) {
                return 'Either baseRoleId or actions (or both) must be provided'
              }
              return true
            },
          }}
        />
      </BorderlessFormWrapper>
      {isErrorCreateRole &&
        !!errorCreateRole &&
        typeof errorCreateRole === 'object' &&
        'message' in errorCreateRole &&
        typeof errorCreateRole.message === 'string' && (
          <SubmitError>{errorCreateRole.message}</SubmitError>
        )}
    </LoadingContainer>
  )
}

export default CreateRoleForm

const Loading = styled.div`
  color: ${(p) => p.theme.text};
`

const BorderlessFormWrapper = styled.div`
  /* Removes border and top padding from DynamicForm's Container for better consistency */
  > div {
    border: none;
    padding-top: 0;
  }

  /* Limit actions JsonInput textarea to ~50 lines with vertical scroll and proper padding */
  .field-input textarea {
    position: relative;
    padding-top: 12px;
    padding-bottom: 12px;
    min-height: calc(
      30 * 1.4 * 12px + 24px
    ); /* 30 lines * line-height * font-size + padding */

    overflow-y: auto;
  }

  /* Fix the textarea wrapper Box to not use absolute positioning trick */
  .field-input > div > div:first-child {
    min-height: auto !important;
    max-height: none;
  }

  .field-input > div > div:first-child > div:last-of-type {
    display: none; /* Hide the GrowBox used for auto-resize */
  }
`

const SubmitError = styled(ErrorComponent)`
  margin-top: 16px;
`
